import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const NeuralAnimation = () => {
  // Generate random positions for the dots
  const generateDots = () => {
    // Assign each dot a random color from our palette
    const colors = [
      'rgb(147, 51, 234)', // Purple
      'rgb(168, 85, 247)', // Light Purple
      'rgb(59, 130, 246)', // Blue
      'rgb(236, 72, 153)'  // Pink
    ];
    
    return Array.from({ length: 8 }, (_, i) => ({
      id: i,
      x: 20 + Math.random() * 160,
      y: 20 + Math.random() * 160,
      dx: (Math.random() - 0.5) * 4.5,
      dy: (Math.random() - 0.5) * 4.5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
  };

  const [dots, setDots] = useState(generateDots());

  // Animate dots with faster interval
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prevDots => prevDots.map(dot => {
        let newX = dot.x + dot.dx;
        let newY = dot.y + dot.dy;
        
        if (newX < 20 || newX > 180) dot.dx *= -1;
        if (newY < 20 || newY > 180) dot.dy *= -1;
        
        return {
          ...dot,
          x: newX < 20 ? 20 : newX > 180 ? 180 : newX,
          y: newY < 20 ? 20 : newY > 180 ? 180 : newY
        };
      }));
    }, 20); // Even faster interval (was 30ms)

    return () => clearInterval(interval);
  }, []);

  // Draw connections between nearby dots
  const getConnections = () => {
    const connections = [];
    for (let i = 0; i < dots.length; i++) {
      for (let j = i + 1; j < dots.length; j++) {
        const dx = dots[i].x - dots[j].x;
        const dy = dots[i].y - dots[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < 60) {
          connections.push({
            id: `${i}-${j}`,
            x1: dots[i].x,
            y1: dots[i].y,
            x2: dots[j].x,
            y2: dots[j].y,
            opacity: 1 - (distance / 60),
            // Create gradient between the two dot colors
            color1: dots[i].color,
            color2: dots[j].color
          });
        }
      }
    }
    return connections;
  };

  return (
    <svg viewBox="0 0 200 200" className="w-32 h-32">
      <defs>
        {/* Create unique gradient for each connection */}
        {getConnections().map(conn => (
          <linearGradient
            key={`gradient-${conn.id}`}
            id={`gradient-${conn.id}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor={conn.color1} stopOpacity="0.5" />
            <stop offset="100%" stopColor={conn.color2} stopOpacity="0.5" />
          </linearGradient>
        ))}
      </defs>
      
      {getConnections().map(conn => (
        <line
          key={conn.id}
          x1={conn.x1}
          y1={conn.y1}
          x2={conn.x2}
          y2={conn.y2}
          stroke={`url(#gradient-${conn.id})`}
          strokeWidth="1"
          opacity={conn.opacity}
        />
      ))}
      
      {dots.map(dot => (
        <circle
          key={dot.id}
          cx={dot.x}
          cy={dot.y}
          r="4"
          fill={dot.color}
          className="animate-pulse"
        />
      ))}
    </svg>
  );
};

// Default loading messages
const defaultLoadingMessages = [
  "Analyzing your mood preferences...",
  "Searching through our literary collection...",
  "Curating the perfect book list..."
];

function LoadingModal({ isOpen, messages }) {
  const [messageIndex, setMessageIndex] = useState(0);
  
  // Use provided messages or fall back to defaults
  const displayMessages = messages || defaultLoadingMessages;

  useEffect(() => {
    if (!isOpen) return;

    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    const interval = setInterval(() => {
      setMessageIndex((current) => (current + 1) % displayMessages.length);
    }, 1667);

    return () => {
      // Re-enable scrolling when modal closes
      document.body.style.overflow = 'unset';
      clearInterval(interval);
    };
  }, [isOpen, displayMessages]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 overflow-hidden" style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 99999 }}>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 100000 }}>
        <div className="bg-white rounded-xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <NeuralAnimation />
            </div>
            <h3 className="text-lg font-medium text-gray-900 min-h-[56px] flex items-center justify-center transition-all duration-300">
              {displayMessages[messageIndex]}
            </h3>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LoadingModal; 